import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  ColorPicker,
  message,
  Switch,
  Radio,
  notification,
  Skeleton,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { getBusinessServices, uploadImage } from "../../../../api/service";
import { createBanner, updateBanner } from "../../../../api/banner";
import colors from "../../../../share/constants/constants";
import Dropdown from "../../../../components/common/dropdown";
import { useDispatch, useSelector } from "react-redux";
import { fetchBusiness, reset } from "../../../../store/slices/businessSlice";
import { fetchSubCategories } from "../../../../store/slices/subCategorySlice";
import { fetchCategories } from "../../../../store/slices/categorySlice";

const CardConfigurator = ({ reloadBanners, formData }) => {
  const [form] = Form.useForm();

  const [value, setValue] = useState("subCategory");
  const [position, setPosition] = useState(true);
  const [selectBusinessId, setSelectBusinessId] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [subCategoryEdit, setSubCategoryEdit] = useState(false);

  const [cardConfig, setCardConfig] = useState(
    formData || {
      backgroundColor: "#512DA8",
      heading: "Add your heading here...",
      headingColor: "#fff",
      description: "Add description here...",
      descriptionColor: "#fff",
      buttonText: "Button Title",
      buttonColor: "#F8AB03",
      buttonTextColor: "#0E0039",
      position: true,
      clickAction: "subCategory",
      imageUrl: "",
    }
  );

  useEffect(() => {
    setValue(formData ? formData?.clickAction : "subCategory");
    if (formData?.position) {
      setPosition(formData?.position === "first" ? true : false);
    }
    if (formData?.clickAction === "subCategory")
      setSelectedCategoryId(formData ? formData?.categoryId : null);

    if (formData?.clickAction === "service") {
      handleBusinessChange(formData?.vendorId);
      setSelectBusinessId(formData?.vendorId);
      setSelectedService(formData?.serviceId);
    }
  }, [formData]);
  useEffect(() => {
    const isSubCategoryEdit = serviceCategories?.some(
      (item) => item?._id === cardConfig?.categoryId
    );
    setSubCategoryEdit(isSubCategoryEdit);
    setValue(!isSubCategoryEdit ? "subCategory" : formData?.clickAction);
  }, [formData]);
  useEffect(() => {
    if (selectedService) {
      form.setFieldsValue({
        serviceId: selectedService?._id,
      });
    }
  }, [selectedService, form]);

  const onChange = (e) => {
    setValue(e.target.value);
  };
  const onEditFinish = async (values) => {
    const payload = {
      ...values,
      isActive: formData?.isActive,
      position: position ? "first" : "second",
      clickAction:
        values.clickAction === "subCategory" ? "category" : values.clickAction,
    };

    try {
      const res = await updateBanner(formData?._id, payload);
      if (res) {
        message.success("Banner updated successfully");
        reloadBanners();
      }
    } catch (error) {}
  };

  const handleFormSubmit = async (values) => {
    try {
      if (!values.imageUrl)
        return notification.error({
          message: "Image is required",
          description: "Please upload an image",
        });
      setCardConfig(values);
      const payload = {
        ...values,
        isActive: true,
        position: values.position === true ? "first" : "second",
        clickAction:
          values.clickAction === "subCategory"
            ? "category"
            : values.clickAction,
      };
      // delete payload.businessId;
      const res = await createBanner(payload);
      if (res) {
        message.success("Banner created successfully");
        reloadBanners();
      }
    } catch (error) {
      message.success("An error occurred");
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    setCardConfig({
      ...allValues,
    });
  };

  const handleColorPicker = (color, btn) => {
    if (btn === "backgroundColor") {
      setCardConfig({ ...cardConfig, backgroundColor: color.toHexString() });
      form.setFieldValue("backgroundColor", color.toHexString());
    } else if (btn === "headingColor") {
      setCardConfig({ ...cardConfig, headingColor: color.toHexString() });
      form.setFieldValue("headingColor", color.toHexString());
    } else if (btn === "descriptionColor") {
      setCardConfig({ ...cardConfig, descriptionColor: color.toHexString() });
      form.setFieldValue("descriptionColor", color.toHexString());
    } else if (btn === "buttonColor") {
      setCardConfig({ ...cardConfig, buttonColor: color.toHexString() });
      form.setFieldValue("buttonColor", color.toHexString());
    } else if (btn === "buttonTextColor") {
      setCardConfig({ ...cardConfig, buttonTextColor: color.toHexString() });
      form.setFieldValue("buttonTextColor", color.toHexString());
    }
  };

  const handleImageUpload = async (event) => {
    try {
      const validImage = beforeUpload(event.file);

      if (!validImage.isJpgOrPng) {
        return message.error("You can only upload JPG/PNG file!");
      } else if (!validImage.isLt2M) {
        return message.error("Image must smaller than 2MB!");
      } else if (event.file && validImage.isJpgOrPng && validImage.isLt2M) {
        const formData = new FormData();
        formData.append("image", event.file);
        const response = await uploadImage(formData);
        if (response) {
          setCardConfig({ ...cardConfig, imageUrl: response.url });
          form.setFieldValue("imageUrl", response.url);
          message.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      console.log(error);
      message.error("Error while uploading image");
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    const isLt2M = file.size / 1024 / 1024 < 2;
    return { isJpgOrPng, isLt2M };
  };

  const dispatch = useDispatch();
  const businessNames = useSelector((state) => state?.business?.res?.items);
  const isBusinessLoading = useSelector((state) => state?.business?.isLoading);
  const {
    res: subCategories,
    isLoading,
    error,
  } = useSelector((state) => state?.subCategory);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    formData ? formData?.categoryId : null
  );
  const [services, setServices] = useState([]);
  const serviceCategories = useSelector((state) => state?.category?.res);
  const loadMoreData = (page, itemPerPage = 10, text = "") => {
    const payload = {
      pageNumber: page,
      itemPerPage: itemPerPage,
    };
    if(text) {
      payload.text = text;
      payload.isLoadMore = true
    }
    dispatch(fetchBusiness(payload));
  };

  useEffect(() => {
    const payload = {
      pageNumber: 1,
      itemPerPage: 50,
    };
    dispatch(fetchBusiness(payload));
  }, []);

  useEffect(() => {
    dispatch(fetchSubCategories());
  }, []);
  useEffect(() => {
    dispatch(fetchCategories());
  }, []);
  const handleBusinessChange = async (businessId) => {
    setSelectBusinessId(businessId);
    setSelectedBusiness(businessNames.find((b) => b._id === businessId));
    setSelectedService(null);
    setServices([]);
    // form.setFieldsValue({ serviceId: undefined });
    await getServicesByBusinessId(businessId, 1);
  };

  const getServicesByBusinessId = async (businessId, page) => {
    setIsServiceLoading(true);
    const res = await getBusinessServices({
      pageNumber: page,
      pageSize: 50,
      businessId: businessId,
    });
    if (res) {
      setServices((prevServices) => [...prevServices, ...res?.items]);
    }
    if (res && formData && formData?.clickAction === "service") {
      setSelectedService(formData?.serviceId);
    }
    setIsServiceLoading(false);
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategoryId(categoryId);
  };

  const loadMoreServiceData = async (page) => {
    await getServicesByBusinessId(selectBusinessId, page);
  };

  const handleServiceChange = (serviceId) => {
    const tt = services.find((s) => s._id === serviceId);
    setSelectedService(tt);
  };

  const fetchBusinessByName = (payload) => {
    if(payload?.pageNumber === 1 && payload?.text) {
      dispatch(reset());
    }
    dispatch(fetchBusiness(payload));
  }

  const onDropDownClose = () => {
    dispatch(reset());
    const payload = {
      pageNumber: 1,
      itemPerPage: 10,
    };
    dispatch(fetchBusiness(payload));
  }

  const renderFormFields = () => {
    switch (value) {
      case "subCategory":
        return (
          <Form.Item
            name="categoryId"
            label="Choose Sub-Category"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Dropdown
              className={"h-5 !rounded-lg"}
              placeholder="Choose Sub-Category"
              loadMoreData={(e) => {}}
              defaultValue={value === "subCategory" ? selectedCategoryId : null}
              key={value}
              data={subCategories}
              onChange={handleCategoryChange}
              loader={isLoading}
            />
          </Form.Item>
        );

      case "category":
        return (
          <Form.Item
            name="categoryId"
            label="Choose Category"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Dropdown
              className={"h-5 !rounded-lg"}
              placeholder="Choose Category"
              loadMoreData={(e) => {}}
              key={value}
              defaultValue={
                value === "category" && subCategoryEdit
                  ? selectedCategoryId
                  : null
              }
              data={serviceCategories}
              onChange={handleCategoryChange}
              loader={isLoading}
            />
          </Form.Item>
        );
      case "service":
        return (
          <>
            <Form.Item
              name="vendorId"
              label="Choose Vendor for Services"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"h-5 !rounded-lg"}
                placeholder="Choose Vendor for Services"
                loadMoreData={(e, itemParPage, text) => {
                  loadMoreData(e, itemParPage, text);
                }}
                data={businessNames}
                onChange={handleBusinessChange}
                loader={isBusinessLoading}
                defaultValue={selectBusinessId}
                isBusiness={true}
                fetchData={fetchBusinessByName}
                onDropDownClose={onDropDownClose}
              />
            </Form.Item>
            <Form.Item
              name="serviceId"
              label="Choose Service or Deal"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"h-5 !rounded-lg"}
                placeholder="Choose Service"
                  // isService={true} ??!
                defaultValue={selectedService?._id}
                loadMoreData={(e) => {
                  loadMoreServiceData(e);
                }}
                data={services}
                onChange={handleServiceChange}
                loader={isServiceLoading}
              />
            </Form.Item>
          </>
        );
      case "tesla":
        return <></>;
      case "web-link":
        return (
          <Form.Item
            label="Link"
            name="link"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Input />
          </Form.Item>
        );
      default:
        return null;
    }
  };
  const initialValues = {
    ...cardConfig,
    clickAction: !subCategoryEdit ? "subCategory" : cardConfig.clickAction,
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={(values) =>
          formData && formData?._id
            ? onEditFinish(values)
            : handleFormSubmit(values)
        }
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          label="Background Color"
          name="backgroundColor"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ColorPicker
            defaultValue={cardConfig.backgroundColor}
            onChange={(color) => handleColorPicker(color, "backgroundColor")}
          />
        </Form.Item>

        <Form.Item
          label="Heading"
          name="heading"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Heading Color"
          name="headingColor"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ColorPicker
            defaultValue={cardConfig.headingColor}
            onChange={(color) => handleColorPicker(color, "headingColor")}
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          label="Description Color"
          name="descriptionColor"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ColorPicker
            defaultValue={cardConfig.descriptionColor}
            onChange={(color) => handleColorPicker(color, "descriptionColor")}
          />
        </Form.Item>

        <Form.Item
          label="Button Text"
          name="buttonText"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Button Color"
          name="buttonColor"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ColorPicker
            defaultValue={cardConfig.buttonColor}
            onChange={(color) => handleColorPicker(color, "buttonColor")}
          />
        </Form.Item>
        <Form.Item
          label="Button Text Color"
          name="buttonTextColor"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <ColorPicker
            defaultValue={cardConfig.buttonTextColor}
            onChange={(color) => handleColorPicker(color, "buttonTextColor")}
          />
        </Form.Item>

        <Form.Item
          label="Upload Image"
          name="imageUrl"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageUpload}
          >
            {cardConfig.imageUrl ? (
              <img
                src={cardConfig.imageUrl}
                alt="uploaded"
                style={{ width: "100%" }}
              />
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item label="Button Click Action" name="clickAction">
          <Radio.Group
            onChange={onChange}
            value={value}
            checkedChildren={value}
          >
            <Radio value={"subCategory"}>Sub-Category</Radio>
            <Radio value={"category"}>Category</Radio>
            <Radio value={"service"}>Service or Deal</Radio>
            <Radio value={"tesla"}>Tesla Deals</Radio>
            <Radio value={"web-link"}>Link</Radio>
          </Radio.Group>
        </Form.Item>

        {renderFormFields()}

        <Form.Item label="Postion" name="position">
          <Switch
            checkedChildren="First Slider"
            unCheckedChildren="Second Slider"
            defaultChecked
            checked={position}
            onChange={(e) => setPosition(e)}
          />
        </Form.Item>

        <Form.Item>
          <Button color={colors.primaryPurple} type="primary" htmlType="submit">
            Save Card
          </Button>
        </Form.Item>
      </Form>

      <div
        style={{
          backgroundColor: cardConfig.backgroundColor,
          padding: "20px",
          borderRadius: "10px",
          marginTop: "20px",
          display: "flex",
          alignItems: "center",
          width: "50%",
        }}
      >
        <div style={{ flex: 1 }}>
          <h2 style={{ color: cardConfig.headingColor }}>
            {cardConfig?.heading}
          </h2>
          <p style={{ color: cardConfig.descriptionColor }}>
            {cardConfig.description}
          </p>
          <Button
            shape="round"
            style={{
              backgroundColor: cardConfig?.buttonColor,
              color: cardConfig?.buttonTextColor,
            }}
          >
            {cardConfig.buttonText}
          </Button>
        </div>

        {cardConfig.imageUrl && (
          <div style={{ flex: 1 }}>
            <img
              src={cardConfig.imageUrl}
              alt="Card"
              style={{ width: "100%", borderRadius: "10px" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CardConfigurator;
